import { defineStore } from 'pinia';
import { ElMessage } from 'element-plus';
import router from '@/router';
import { loginApi, logoutApi, type UserInfoResponse } from '@/api/auth';
import { getProfileDetail } from '@/api/account';
import { i18n } from '@/locales';
import { UserRole, System, SystemRole, SystemProductId } from '@/router/config/roles';
import { redirectToLogin } from '@/utils/auth';
import { UserRoleName, UserRoleNameToIdMap } from '@/router/config/roles';
import {
  getEnterpriseInfo
} from '@/api/company'
import { ProductId } from '@/enum/account';
import {
  getValidToken,
  deleteCookie
} from '@/utils/auth'
import { getSystemType } from '@/config/env';
import {
  routerWhiteList,
  pathIsInRouterWhiteList
} from '@/router/guards';

import { openNoAccessPermissionModal } from '@/components/business-components/dialog';
import { useChatStore } from './chat';
interface UserState {
  id: number;
  token: string;
  roleId: UserRole | null;
  roleIsValidate: boolean;
  roles: string[];
  permissions: string[];
  profile: UserInfoResponse | null;
  products: Account.product[];
  isLoggedIn: boolean;
  userSystem: 'business' | 'agent' | null;
  productId: number;
  chatToken: string;
}
// 获取有效token的函数：优先从cookie获取，如果没有则从localStorage获取
const validRoleIds = Object.values(UserRole).filter((value) => typeof value === 'number') as number[];
export const useUserStore = defineStore('user', {
  state: (): UserState => ({
    id: 0,
    token: getValidToken() || '',
    roleId: null,
    roles: [],
    permissions: [],
    profile: null,
    products: [],
    isLoggedIn: false,
    roleIsValidate: false,
    userSystem: null,
    productId: 2,
    // id 57 pddlll-YXJ 的 token
    chatToken: '',
  }),

  getters: {
    // isLoggedIn: (state) => !!state.token,
    id: (state) => state.profile?.id,
    userName: (state) => state.profile?.name || state.profile?.username || '',
    userEmail: (state) => state.profile?.email || '',
    userAvatar: (state) => state.profile?.avatar || '',
    userEnterpriseId: (state) => state.profile?.enterpriseId || 1,
    userProjectId: (state) => state.profile?.projectId || '',
    userChatToken: (state) => state.profile?.chatToken || '',
  },

  actions: {

    // 登录
    async login(params: { username: string; password: string }) {
      try {
        const response = await loginApi(params);
        
        // 保存 token
        this.token = response.accessToken;
        
        localStorage.setItem('lincId', response.lincId);
        
        // 登录成功后获取用户信息（设置角色）
        await this.getUserInfo();

        ElMessage.success(i18n.global.t('login.loginSuccess'));
        return response;
      } catch (error: any) {
        ElMessage.error(error.message || i18n.global.t('login.loginFailed'));
        throw error;
      }
    },
    getUserProducts() {
      // 从token中解析当前用户的产品权限列表
      let token = this.token
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid token format');
      }

      // 解码 JWT payload 部分
      const payloadBase64 = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
      const decodedPayload = atob(payloadBase64);
      const tokenPayload = JSON.parse(decodedPayload);

      // 检查 product_ids 数组并去重
      const productIds = tokenPayload.product_ids || [];
      const uniqueProductIds = [...new Set(productIds)] as number[]; // 去重
      this.products = uniqueProductIds.map(item => {
        return {
          productId: item,
        }
      })
    },
    checkRolesValidate(roles: any[]) {
      if (!roles || !roles.length) {
        // 没有角色，展示message弹窗
        ElMessage.error(i18n.global.t('common.noSystemAccess'))
        return false;
      } else {
        let rolesMap: {[key: string | number]: Account.RoleInfo} = roles.reduce((pre, cur: Account.RoleInfo) => {
          pre[cur.productId] = cur;
          return pre;
        }, {})
        let curProductId = this.productId; 
        let fullPath = window.location.href;
        if (pathIsInRouterWhiteList(fullPath)) {
          // 如果是白名单的url，直接返回存在的 agent 或者 biz 的角色
          if (rolesMap[SystemProductId.agent]) {
            return rolesMap[SystemProductId.agent]
          } else if (rolesMap[SystemProductId.business]) {
            return rolesMap[SystemProductId.business]
          }
        } else if (curProductId == SystemProductId.agent && !rolesMap[curProductId]) {
          // 当前系统是agent系统，但是当前登录信息中没有agent的角色，认为需要重新登录
          redirectToLogin({ redirectPath: '', delay: 0 });
        } else if (rolesMap[curProductId]) {
          // 有当前系统的权限
          let role = rolesMap[curProductId];
          if (!validRoleIds.includes(role.id)) {
            // 有当前系统的权限但是是非法的角色
            console.log('有当前系统的权限但是是非法的角色')
            return false;
          }
          return rolesMap[curProductId];
        } else if (rolesMap[ProductId.TALENT]) {
          // 只有 TALENT 权限提示
          openNoAccessPermissionModal({
            message: i18n.global.t('common.noCareerAccess'),
            tips: i18n.global.t('common.contactAdminToEnable'),
            confirmButtonText: i18n.global.t('common.enterTalentSystem'),
          }).then(res => {
            window.location.href = import.meta.env.VITE_TALENT_URL;
          });
          // 无当前系统的权限，但是有talent权限
          return false;
        } else {
          redirectToLogin({ redirectPath: '', delay: 0 });
        }
        return false;
      }
    },
    checkHasOtherSystemAuth(roles: any) {
      if (!roles) return null;
      if (roles[ProductId.ATS]) return null;
      if (roles[ProductId.TALENT]) {
        console.log('profile',)
        
        return ProductId.TALENT;
      }
      return null;
    },
    getUserSystem() {
      // 第一优先级：检查环境变量配置的系统类型

      // 第二优先级：使用域名判断（非localhost环境）
      let host = location.host;
      // this.productId = SystemProductId.business;
      // return;
      if (!host.includes('localhost')) {
        // 非开发环境使用域名判断
        if (host.includes(import.meta.env.VITE_CRAEER_BIZ_HOSTNAME)) {
          this.userSystem = System.business;
          this.productId = SystemProductId.business;
          console.log('🌐 通过域名识别: Business');
        } else if (host.includes(import.meta.env.VITE_CRAEER_AGENT_HOSTNAME)) {
          this.userSystem = System.agent;
          this.productId = SystemProductId.agent;
          console.log('🌐 通过域名识别: Agent');
        }
      } else {
        // 第三优先级：开发环境使用角色id判断
        const configSystemType = getSystemType();
        this.userSystem = System.business;
        if (configSystemType) {
          this.userSystem = configSystemType;
          this.productId = SystemProductId[configSystemType];
          console.log(`🔧 使用配置的系统类型: ${configSystemType}`);
          return;
        } else {
          this.userSystem = System.business;
          this.productId = SystemProductId.business;
          console.log(`🔧 使用默认配置的系统类型: ${System.business}`);
        }
        // if (this.roleId) {
        //   if (SystemRole.business.includes(this.roleId)) {
        //     this.userSystem = System.business;
        //     console.log('🏢 通过角色识别: Business');
        //   } else if (SystemRole.agent.includes(this.roleId)) {
        //     this.userSystem = System.agent;
        //     console.log('🤝 通过角色识别: Agent');
        //   } else {
        //     this.userSystem = null;
        //     console.warn('⚠️ 无效的用户系统');
        //   }
        // }
      }
    },
    // 获取用户信息
    async getUserInfo() {
      try {
        if (!this.token) {
          throw new Error('未登录');
        } 
        // 判断所属系统
        this.getUserSystem()
        const profileDetail = await getProfileDetail();
        const enterpriseInfo = await getEnterpriseInfo();

        let resRoles = profileDetail.roles || [];
        // this.products = resRoles;
        this.getUserProducts();
        let userRole: Account.RoleInfo | false = this.checkRolesValidate(resRoles);
        // console.log('userRole', userRole)
        // let userRole: Account.RoleInfo | false = {
        //   id: 7,
        //   name: 'ROLE_AGENT_ADMIN'
        // };
        
        // 将新API返回的数据映射到UserInfoResponse格式
        const userInfo: UserInfoResponse = {
          id: String(profileDetail.id),
          username: profileDetail.name,
          email: profileDetail.email,
          name: profileDetail.name,
          enterpriseId: String(profileDetail.enterpriseId),
          enterpriseName: enterpriseInfo?.name,
          roleInfo: {
            id: userRole?.id, // 根据实际情况映射角色
            name: userRole?.name
          },
          chatToken: profileDetail.chatToken,
        };
        // console.log('userInfo', userInfo)
        // 保存旧角色以检测变化
        const oldRoles = userInfo.roleInfo?.id;
        this.roleIsValidate = !!userRole;
        this.profile = userInfo;
        this.roleId = userInfo.roleInfo?.id;
        this.isLoggedIn = true;


        // 如果角色发生变化，重新生成权限路由以触发菜单更新
        // const { usePermissionStore } = await import('./permission');
        // const { asyncRoutes } = await import('@/router/routes/async');
        // const permissionStore = usePermissionStore();
        // permissionStore.generateRoutes(this.roleId, asyncRoutes);

        return userInfo;
      } catch (error: any) {
        console.log('error', error.message?.includes('401') || error.message?.includes('unauthorized'))
        // 如果获取用户信息失败，可能是 token 过期
        if (error.message?.includes('401') || error.message?.includes('unauthorized')) {
          this.logout();
        }
        throw error;
      }
    },
    async clearUserInfo() {

      try {
        // 关闭 StreamChat 长连接，防止旧用户 token 的 WebSocket 继续监听
        try {
          await useChatStore().disconnect()
        } catch {
          // 连接本就不存在时忽略
        }
        // 调用登出接口
        if (this.token) {
          await logoutApi();
        }
      } catch (error) {
        console.error('登出接口调用失败:', error);
      } finally {
        // 清空用户状态
        this.token = '';
        this.roleId = null;
        this.roles = [];
        this.permissions = [];
        this.profile = null;
        this.userSystem = null;
        // this.productId = 2;
        // 清空本地存储
        localStorage.removeItem('lincId');
        deleteCookie();
      }
    },
    // 登出
    async logout(errorCode?: string) {
      let fullPath = window.location.href;
      if (!pathIsInRouterWhiteList(fullPath)) {
        if (errorCode === 'C0012') {
          ElMessage.info(i18n.global.t('common.authChange'));
        } else if (errorCode === 'C0007') {
          ElMessage.info(i18n.global.t('common.tokenExpired'));
        } else if (errorCode === 'logout') {
          ElMessage.info(i18n.global.t('common.logouting'));
        } else {
          ElMessage.info(i18n.global.t('common.pleaseLogin'));
        }
        try {
          // 调用登出接口
          await this.clearUserInfo();
        } catch (error) {
          console.error('登出接口调用失败:', error);
        } finally {
          // 重置权限路由状态，确保重新登录时会重新生成路由
          // const { usePermissionStore } = await import('./permission');
          // const permissionStore = usePermissionStore();
          // permissionStore.resetRoutes();

          // 跳转到登录页（使用统一的登录跳转逻辑，支持外部登录URL）
          
        }
        // 白名单页面不做登录跳转
        redirectToLogin({ redirectPath: fullPath, delay: 0 });

      
      } else {
        console.log('白名单页面不跳转登录')
      }
    },

    // 初始化用户状态（应用启动时调用）
    async initUserState() {
      if (this.token && !this.profile) {
        // TODO: 获取用户信息接口完成后，取消注释以下代码
        // try {
        //   await this.getUserInfo();
        // } catch (error) {
        //   console.error('初始化用户状态失败:', error);
        //   this.logout();
        // }
      }
    }
  }
});
